import type { AppStore } from "@/store"
import { memberRemovalPath } from "@/lib/member-removal"
import { grantRemovalPath } from "@/lib/grant-removal"
import { currentStaffSession } from "@/store/staffSessionSlice"
import { assertStaffWriteAvailable, captureStaffEpoch } from "@/services/staffTransportSession"
import { commandFailure, StaffCommandNotSentError, type StaffCommandOwner } from "@/services/staffCommands"
import { benefitEditorActions as actions, commandKey, type BenefitEditorKind, type BenefitEditor, type EditorAddress, type StaffCommandRequest, type GrantView, type GrantInformationTab, type BenefitToolSelection } from "./benefitEditorSlice"
import { benefitReadKey } from "./benefitReadSlice"
import { captureEditorDraft, editorGroup, hasEditorField, type DraftGroup, type DraftField, type FieldsFor, type EditorKey } from "./benefitEditorDrafts"
export type { EditorKey } from "./benefitEditorDrafts"
const safeClone = <T>(value: T): T => structuredClone(value)
export { commandFailure } from "@/services/staffCommands"

/** Explicit financial retry only, with the identical sealed body and server-supported key. */
function supportsSameAttemptRetry(request: StaffCommandRequest) {
  if (request.method !== "POST" || !/\/grants\/\d+\/\d+\/(review(?:\/(?:returned-recovery|certification-correction))?|replacement)$/.test(request.path)) return false
  try { const body = JSON.parse(request.body ?? "null"); return typeof body?.idempotencyKey === "string" && body.idempotencyKey.length > 0 }
  catch { return false }
}
function permits(active: BenefitEditor, request: StaffCommandRequest) {
  const { kind } = active
  const path = new URL(request.path, "https://staff.invalid").pathname
  if (active.kind === "grant-removal") {
    const removal = active.fields.confirmation
    return !!removal && request.method === "DELETE" && path === grantRemovalPath(removal)
  }
  if (active.kind === "remove-member") {
    const removal = active.fields.confirmation
    return !!removal && removal.benefitId === active.benefitId && removal.concurrencyToken === active.token
      && request.method === "DELETE" && request.path === memberRemovalPath(removal) && request.body === undefined
  }
  if (["member", "member-income"].includes(kind)) {
    const personId = active.key.slice(kind.length + 1)
    const root = `/api/benefitapplications/${encodeURIComponent(active.benefitId)}/household-members/${encodeURIComponent(personId)}`
    return !!personId && (kind === "member-income" ? path === `${root}/income` && request.method === "PUT"
      : path === root && request.method === "PATCH")
  }
  const suffixes: Partial<Record<BenefitEditorKind, readonly string[]>> = {
    intake: ["/intake-details"], addresses: ["/application-details"], housing: ["/household-information"],
    member: ["/household-members/"], "add-member": ["/household-members"],
    "member-income": ["/household-members/"], "household-income": ["/household-income"], "no-income": ["/income/no-income"],
    "non-cash": ["/non-cash-benefits"], questionnaire: ["/questionnaire"], "energy-bill": ["/energy-bill-information"],
    "energy-intake": ["/energy-intake"], "requested-benefits": ["/requested-benefits"], "target-year": ["/target-year"],
    "applicant-correction": ["/applicant-household-correction"], ownership: ["/ownership"],
    grant: ["/grants"], "grant-task": ["/grants"], "grant-review": ["/grants"], "vendor-payment": ["/grants"], "payment-split": ["/grants"],
    workflow: ["/workflow/"], lifecycle: ["/transitions", "/recertify", "/workflow/"],
  }
  return (suffixes[kind] ?? []).some(suffix => path.includes(suffix))
}

export function createBenefitEditorController(store: AppStore, address: EditorAddress,
  current: () => { readStamp: string | null; writable: boolean; section?: string },
  effects: { refresh(): void; clearWorkspace(): void }) {
  const sessionEpoch = store.getState().staffSession.epoch
  const fields = new Map<DraftGroup, Map<string, { value(): unknown; fresh(): unknown }>>()
  const owned = () => {
    if (store.getState().staffSession.epoch !== sessionEpoch) return null
    const active = store.getState().benefitEditors.active
    return active?.scopeKey === address.scopeKey && active.benefitId === address.benefitId ? active : null
  }
  const refresh = () => { if (address.benefitId !== "new") effects.refresh() }
  const run: StaffCommandOwner = async (request, send) => {
    const session = currentStaffSession(store.getState()), epoch = captureStaffEpoch()
    if (!session || session.scopeKey !== address.scopeKey || session.epoch !== sessionEpoch) throw new StaffCommandNotSentError("editor-unavailable")
    assertStaffWriteAvailable(request.path)
    const active = owned()
    if (active && !permits(active, request)) throw new StaffCommandNotSentError("command-busy")
    const previous = store.getState().benefitEditors.commands[commandKey(address)]
    const supportedRetry = previous?.phase === "outcomeUnknown" && supportsSameAttemptRetry(request)
      && JSON.stringify(previous.request) === JSON.stringify(request)
    const id = supportedRetry ? previous!.id : crypto.randomUUID()
    store.dispatch(actions.commandRequested({ ...address, id, editorSessionId: active?.sessionId ?? null,
      request: safeClone(request), phase: "saving", message: null, supportedRetry }))
    const accepted = store.getState().benefitEditors.commands[commandKey(address)]
    if (accepted?.id !== id || accepted.phase !== "saving" || (!supportedRetry && previous?.id === id)) throw new StaffCommandNotSentError("command-busy")
    const sealed = accepted.request!
    try {
      const result = await send(sealed)
      if (captureStaffEpoch() === epoch) {
        store.dispatch(actions.commandConfirmed({ ...address, id }))
        refresh()
      }
      return result
    } catch (error) {
      if (captureStaffEpoch() === epoch) {
        const failure = commandFailure(error)
        store.dispatch(actions.commandRejected({ ...address, id, ...failure }))
        if (failure.outcome === "denied" && address.benefitId !== "new") {
          effects.clearWorkspace()
        }
        if (["conflict", "unknown", "denied"].includes(failure.outcome)) refresh()
      }
      throw error
    }
  }
  return {
    store, address, run, owned, current,
    incomeHistoryType() {
      if (store.getState().staffSession.epoch !== sessionEpoch) return null
      const preference = store.getState().benefitEditors.incomeHistoryPreference
      return preference?.scopeKey === address.scopeKey && preference.benefitId === address.benefitId ? preference.incomeType : null
    },
    confirmIncomeHistoryType(incomeType: number) {
      const active = owned(), state = store.getState()
      if (!active || active.kind !== "household-income" || active.phase !== "editing"
        || Number(active.fields.incomeType) !== incomeType || !current().writable
        || state.benefitReads.resources[benefitReadKey({ scopeKey: address.scopeKey, id: address.benefitId })]?.phase === "accessDenied") return false
      store.dispatch(actions.incomePreferenceConfirmed({ sessionId: active.sessionId }))
      const preference = store.getState().benefitEditors.incomeHistoryPreference
      return preference?.scopeKey === address.scopeKey && preference.benefitId === address.benefitId && preference.incomeType === incomeType
    },
    toolView() {
      if (store.getState().staffSession.epoch !== sessionEpoch) return null
      const view = store.getState().benefitEditors.toolView
      return view?.scopeKey === address.scopeKey && view.benefitId === address.benefitId ? view : null
    },
    selectToolView(view: BenefitToolSelection) {
      const state = store.getState()
      if (state.staffSession.epoch !== sessionEpoch
        || state.benefitReads.resources[benefitReadKey({ scopeKey: address.scopeKey, id: address.benefitId })]?.phase === "accessDenied") return
      store.dispatch(actions.toolViewSelected({ ...address, ...view }))
    },
    closeToolView(section: BenefitToolSelection["section"]) {
      if (store.getState().staffSession.epoch !== sessionEpoch) return
      store.dispatch(actions.toolViewClosed({ ...address, section }))
    },
    grantView() {
      if (store.getState().staffSession.epoch !== sessionEpoch) return null
      const view = store.getState().benefitEditors.grantView
      return view?.scopeKey === address.scopeKey && view.benefitId === address.benefitId ? view : null
    },
    selectGrantView(view: Omit<GrantView, keyof EditorAddress>) {
      if (store.getState().staffSession.epoch !== sessionEpoch) return
      store.dispatch(actions.grantViewSelected({ ...address, ...view }))
    },
    changeGrantTab(tab: GrantInformationTab) {
      if (store.getState().staffSession.epoch !== sessionEpoch) return
      store.dispatch(actions.grantViewTabChanged({ ...address, tab }))
    },
    closeGrantView() {
      if (store.getState().staffSession.epoch !== sessionEpoch) return
      store.dispatch(actions.grantViewClosed(address))
    },
    retryRequest() {
      const previous = store.getState().benefitEditors.commands[commandKey(address)]
      return previous?.phase === "outcomeUnknown" && previous.request && supportsSameAttemptRetry(previous.request) ? previous.request : null
    },
    register<G extends DraftGroup, F extends DraftField<G>>(group: G, field: F, source: { value(): NoInfer<FieldsFor<G>[F]>; fresh(): NoInfer<FieldsFor<G>[F]> }) {
      let registry = fields.get(group)
      if (!registry) fields.set(group, registry = new Map())
      registry.set(field, source)
      return () => { if (registry!.get(field) === source) registry!.delete(field) }
    },
    open(key: EditorKey, token: string | null) {
      if (store.getState().staffSession.epoch !== sessionEpoch || !current().writable) return false
      const active = store.getState().benefitEditors.active
      if (active) return active.key === key && !!owned()
      const group = editorGroup(key)
      const draft = captureEditorDraft(key, fields.get(group) ?? new Map())
      if (!draft) return false
      const sessionId = crypto.randomUUID()
      store.dispatch(actions.openRequested({ ...address, ...draft,
        sessionId, token, readStamp: current().readStamp, section: current().section ?? "" }))
      queueMicrotask(() => { store.dispatch(actions.openingCompleted({ sessionId })) })
      return owned()?.sessionId === sessionId
    },
    close(key: EditorKey) {
      const active = owned()
      if (!active || active.key !== key) return true
      store.dispatch(actions.closeRequested({ sessionId: active.sessionId }))
      return !owned()
    },
    latestFields(active: BenefitEditor): Record<string, unknown> {
      return Object.fromEntries([...(fields.get(active.group) ?? [])].filter(([name]) => hasEditorField(active.kind, name)).map(([name, source]) => [name, source.fresh()]))
    },
  }
}
export type BenefitEditorController = ReturnType<typeof createBenefitEditorController>
